import React, { ReactNode } from 'react';
import classNames from 'classnames';

export interface BreadcrumbProps {
  children: ReactNode;
  className?: string;
  id?: string;
  wrap?: boolean;
  'rdfa-metadata'?: boolean;
}

interface RdfaMetaDataMap {
  vocab?: string;
  listTypeof?: string;
  listItemProperty?: string;
  listItemTypeof?: string;
  linkProperty?: string;
  linkTypeof?: string;
  spanProperty?: string;
}
const rdfaMetaDataMap: RdfaMetaDataMap = {
  vocab: 'https://schema.org/',
  listTypeof: 'BreadcrumbList',
  listItemProperty: 'itemListElement',
  listItemTypeof: 'ListItem',
  linkProperty: 'item',
  linkTypeof: 'WebPage',
  spanProperty: 'name',
};

/**
 * The Breadcrumb Component based on [The USWDS Breadcrumb component](https://designsystem.digital.gov/components/breadcrumb/)
 */
const Breadcrumb = ({ children, className, id, wrap, 'rdfa-metadata': rdfa }: BreadcrumbProps): JSX.Element => {
  const rdfaMetaData = rdfa ? rdfaMetaDataMap : {};
  return (
    <nav
      id={id}
      aria-label="Breadcrumbs"
      className={classNames(
        'usa-breadcrumb',
        {
          'usa-breadcrumb--wrap': wrap,
        },
        className,
      )}
    >
      <ol className="usa-breadcrumb__list" vocab={rdfaMetaData.vocab} typeof={rdfaMetaData.listTypeof}>
        {React.Children.map(children, (child, index) => {
          if (!child) {
            return null;
          }
          const current = React.Children.count(children) === index + 1;
          const breadcrumbLink = React.isValidElement(child) ? (
            React.cloneElement(
              child as React.ReactElement,
              {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                className: classNames('usa-breadcrumb__link', child.props?.className),
                property: rdfaMetaData.linkProperty,
                typeof: rdfaMetaData.linkTypeof,
              },
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              <span property={rdfaMetaData.spanProperty}>{child.props?.children}</span>,
            )
          ) : (
            <span property={rdfaMetaData.spanProperty}>{child}</span>
          );
          return (
            <li
              className={classNames('usa-breadcrumb__list-item', {
                'usa-current': current,
              })}
              aria-current={current ? 'page' : undefined}
              property={rdfaMetaData.listItemProperty}
              typeof={rdfaMetaData.listItemTypeof}
            >
              {breadcrumbLink}
              {rdfa ? <meta property="position" content={String(index + 1)} /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
